"use client";

import { Translate } from "@/components/Translate";
import { env } from "@/env.mjs";
import Script from "next/script";
import { useEffect, useRef } from "react";
import styles from "./VolumentalWidget.module.css";

const VOLUMENTAL_SDK_SRC = "https://js.volumental.com/sdk/v1/volumental.js";

// Chart ids Volumental has ingested. The tool only renders for products whose chart-id is in
// this list; add ids here as more charts are shared with Volumental.
const SUPPORTED_CHART_IDS = [
	"188",
	"ARCTIC",
	"CB",
	"DK",
	"ETD-385",
	"ETD-738-1",
	"EXTREME",
	"EXTREME 2.0",
	"FROST FIGHTER",
	"FX-100",
	"GARY-BAREFOOT",
	"GDV",
	"GEN-002",
	"HRL",
	"HS",
	"ICEFIGHTER",
	"JAD",
	"JUNKYARD",
	"LC003",
	"LD",
	"MC",
	"OJ-BAREFOOT",
	"OJ-CHR",
	"SDR",
	"SK",
	"SL",
	"TJ",
	"TTT",
	"TTT-2",
	"ULTRA",
	"Vibram",
	"VIK-03008H",
	"VIK-06027",
	"VIK-08",
	"VIK-25004",
	"VK-2032R",
	"VK-2034",
	"VK-2038",
	"VK-2046",
	"VK-2048H",
	"VK-2054",
	"VK-2054H",
	"VK-2065",
	"VK-2072H",
	"WT-09",
];
const supportedChartIds = new Set(SUPPORTED_CHART_IDS.map((id) => id.toLowerCase()));

/** Whether Volumental has ingested this chart-id (and so the widget will render). */
export const isVolumentalSupported = (chartId?: string) =>
	!!chartId && supportedChartIds.has(chartId.toLowerCase());

// Adult-size charts. The description under the widget is kids-sizing copy, so it's hidden for these.
const ADULT_CHART_IDS = [
	"188",
	"ARCTIC",
	"CB",
	"ETD-385",
	"ETD-738-1",
	"FX-100",
	"GDV",
	"GEN-002",
	"HRL",
	"HS",
	"ICEFIGHTER",
	"JAD",
	"JUNKYARD",
	"LD",
	"SL",
	"TJ",
	"TTT",
	"TTT-2",
	"Vibram",
	"VK-2032R",
	"VK-2038",
	"VK-2046",
];
const adultChartIds = new Set(ADULT_CHART_IDS.map((id) => id.toLowerCase()));

const isAdultChart = (chartId?: string) => !!chartId && adultChartIds.has(chartId.toLowerCase());

export interface VolumentalRecommendationDetail {
	length?: string;
	width?: string;
	sizeLocale?: string;
}

interface VolumentalRecommendationProps {
	productId: string;
	chartId?: string;
	/** Fired with Volumental's recommended size (`volumental:on-recommendation`) after a scan. */
	onRecommendation?: (detail: VolumentalRecommendationDetail) => void;
}

export const VolumentalRecommendation = ({
	productId,
	chartId,
	onRecommendation,
}: VolumentalRecommendationProps) => {
	const clientId = env.NEXT_PUBLIC_VOLUMENTAL_CLIENT_ID;
	const containerRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (!onRecommendation) return;
		const el = containerRef.current?.querySelector("volumental-recommendation");
		if (!el) return;
		const handler = (e: Event) => onRecommendation((e as CustomEvent).detail ?? {});
		el.addEventListener("volumental:on-recommendation", handler);
		return () => el.removeEventListener("volumental:on-recommendation", handler);
	}, [onRecommendation]);

	if (!clientId || !productId) return null;
	if (!isVolumentalSupported(chartId)) return null;

	return (
		<div className={styles.container} ref={containerRef}>
			<Script src={VOLUMENTAL_SDK_SRC} strategy="afterInteractive" data-client-id={clientId} />
			<volumental-recommendation
				class="vol-widget"
				component-type="sizechart-button"
				size-locale="EU"
				product-id={productId}
				chart-id={chartId}
			/>
			{!isAdultChart(chartId) && (
				<p className={styles.description}>
					<Translate>volumentalDescription</Translate>
				</p>
			)}
		</div>
	);
};

type VolumentalRecommendationAttrs = React.HTMLAttributes<HTMLElement> & {
	class?: string;
	"component-type"?: "sizechart-button";
	"size-locale"?: string;
	"product-id"?: string;
	"chart-id"?: string;
};

declare module "react" {
	namespace JSX {
		interface IntrinsicElements {
			"volumental-recommendation": VolumentalRecommendationAttrs;
		}
	}
}
